/**
 * Copyright © 2024-2026 Entangled Harmony Labs, LLC
 * All Rights Reserved. Proprietary and Confidential.
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { CheckCircle, XCircle, Target, RefreshCw } from 'lucide-react';
import { FretPosition } from '../Fretboard';
import { QuizScore } from './FretboardQuiz';
import MobileQuizBanner from './MobileQuizBanner';

/** State exposed to parent for rendering mobile banner */
export interface QuizBannerState {
  questionText: string;
  score: { correct: number; total: number };
  feedback: 'correct' | 'incorrect' | null;
  hasAnswers: boolean;
  onNext: () => void;
  onShowAnswer: () => void;
  onClear: () => void;
}

interface NoteRecognitionQuizProps {
  tuning: string[];
  fretRange: { start: number; end: number };
  userFretboardAnswers: FretPosition[];
  setUserFretboardAnswers: (answers: FretPosition[]) => void;
  setHighlightedFretPositions: (positions: FretPosition[]) => void;
  correctUserFretboardAnswers: FretPosition[];
  setCorrectUserFretboardAnswers: (positions: FretPosition[]) => void;
  incorrectUserFretboardAnswers: FretPosition[];
  setIncorrectUserFretboardAnswers: (positions: FretPosition[]) => void;
  updateScore: (correct: number, total: number, streak: number) => void;
  currentScore: QuizScore;
  /** When true, hides the full Card UI (parent renders banner instead) */
  isMobile?: boolean;
  /** Callback to expose quiz state to parent for mobile banner rendering */
  onQuizStateChange?: (state: QuizBannerState) => void;
}

interface NoteQuestion {
  targetNote: string;
  targetString?: number; // For "find X on Y string" questions
  targetStringName?: string; // The actual string name (e.g., 'E', 'A', etc.)
  targetFret?: number; // For "what note is at fret X on string Y" questions
  questionType: 'findNoteOnString' | 'findAllNotes';
  correctPositions: FretPosition[];
}

const NoteRecognitionQuiz: React.FC<NoteRecognitionQuizProps> = ({
  tuning,
  fretRange,
  userFretboardAnswers,
  setUserFretboardAnswers,
  setHighlightedFretPositions,
  correctUserFretboardAnswers,
  setCorrectUserFretboardAnswers,
  incorrectUserFretboardAnswers,
  setIncorrectUserFretboardAnswers,
  updateScore,
  currentScore,
  isMobile = false,
  onQuizStateChange
}) => {
  const [currentQuestion, setCurrentQuestion] = useState<NoteQuestion | null>(null);
  const [showAnswer, setShowAnswer] = useState(false);
  const [feedback, setFeedback] = useState<'correct' | 'incorrect' | null>(null);
  const [immediateFeedback, setImmediateFeedback] = useState<{position: FretPosition, isCorrect: boolean} | null>(null);
  const [localCorrectAnswers, setLocalCorrectAnswers] = useState<FretPosition[]>([]);
  const [localIncorrectAnswers, setLocalIncorrectAnswers] = useState<FretPosition[]>([]);

  // All natural notes and their enharmonic equivalents
  const notes = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
  const naturalNotes = ['C', 'D', 'E', 'F', 'G', 'A', 'B'];

  // Calculate what note is at a specific fret on a string
  const calculateNoteAtFret = (openNote: string, fret: number): string => {
    const flatToSharp: { [key: string]: string } = {
      'Db': 'C#', 'Eb': 'D#', 'Gb': 'F#', 'Ab': 'G#', 'Bb': 'A#'
    };
    
    const normalizedOpenNote = flatToSharp[openNote] || openNote;
    const openIndex = notes.indexOf(normalizedOpenNote);
    
    if (openIndex === -1) return openNote;
    
    const targetIndex = (openIndex + fret) % 12;
    return notes[targetIndex];
  };

  // Find all positions of a note on the fretboard
  const findNotePositions = (targetNote: string, targetString?: number, stringName?: string): FretPosition[] => {
    const positions: FretPosition[] = [];
    let stringsToSearch: number[];

    if (targetString) {
      // If we have a specific string index, use it
      stringsToSearch = [targetString - 1];
    } else if (stringName) {
      // If we have a string name, find all strings with that name (e.g., both E strings)
      stringsToSearch = tuning.map((note, index) => note === stringName ? index : -1).filter(index => index !== -1);
    } else {
      // Search all strings
      stringsToSearch = Array.from({ length: tuning.length }, (_, i) => i);
    }

    stringsToSearch.forEach((stringIndex) => {
      if (stringIndex < tuning.length) {
        for (let fret = fretRange.start; fret <= fretRange.end; fret++) {
          const noteAtFret = calculateNoteAtFret(tuning[stringIndex], fret);
          if (noteAtFret === targetNote) {
            positions.push({
              string: stringIndex + 1,
              fret: fret,
              note: targetNote
            });
          }
        }
      }
    });

    return positions;
  };

  // Generate a new question
  const generateNewQuestion = () => {
    const questionTypes: NoteQuestion['questionType'][] = [
      'findNoteOnString', 
      'findAllNotes'
    ];
    
    const randomType = questionTypes[Math.floor(Math.random() * questionTypes.length)];
    
    let newQuestion: NoteQuestion;

    switch (randomType) {
      case 'findNoteOnString':
        // "Find C# on the G string"
        const randomNote = naturalNotes[Math.floor(Math.random() * naturalNotes.length)];
        const randomString = Math.floor(Math.random() * tuning.length) + 1;
        const stringName = tuning[randomString - 1];
        const possibleNotes = [randomNote, `${randomNote}#`].filter(note => 
          findNotePositions(note, undefined, stringName).length > 0
        );
        const finalNote = possibleNotes[Math.floor(Math.random() * possibleNotes.length)] || randomNote;
        
        newQuestion = {
          targetNote: finalNote,
          targetString: randomString,
          targetStringName: stringName,
          questionType: 'findNoteOnString',
          correctPositions: findNotePositions(finalNote, undefined, stringName)
        };
        break;

      case 'findAllNotes':
        // "Find all F notes on the fretboard"
        const allNotesTarget = naturalNotes[Math.floor(Math.random() * naturalNotes.length)];
        newQuestion = {
          targetNote: allNotesTarget,
          questionType: 'findAllNotes',
          correctPositions: findNotePositions(allNotesTarget)
        };
        break;

      default:
        // Fallback
        const fallbackStringName = tuning[0]; // First string
        newQuestion = {
          targetNote: 'C',
          targetString: 1,
          targetStringName: fallbackStringName,
          questionType: 'findNoteOnString',
          correctPositions: findNotePositions('C', undefined, fallbackStringName)
        };
    }

    setCurrentQuestion(newQuestion);
    setShowAnswer(false);
    setFeedback(null);
    // Don't clear immediateFeedback here - let it persist briefly for user to see
    setTimeout(() => {
      setImmediateFeedback(null);
    }, 100); // Clear feedback after a brief delay
    setUserFretboardAnswers([]);
    setHighlightedFretPositions([]);
    setCorrectUserFretboardAnswers([]);
    setIncorrectUserFretboardAnswers([]);
    setLocalCorrectAnswers([]);
    setLocalIncorrectAnswers([]);
  };

  // Check user's answer
  const checkAnswer = () => {
    if (!currentQuestion || userFretboardAnswers.length === 0) return;

    let isCorrect = false;

    switch (currentQuestion.questionType) {
      case 'findNoteOnString':
        // For single position questions, check if user clicked any correct position
        isCorrect = userFretboardAnswers.some(userPos => 
          currentQuestion.correctPositions.some(correctPos =>
            correctPos.string === userPos.string && correctPos.fret === userPos.fret
          )
        );
        break;

      case 'findAllNotes':
        // For "find all" questions, user needs to find at least half of the positions
        const foundCount = userFretboardAnswers.filter(userPos =>
          currentQuestion.correctPositions.some(correctPos =>
            correctPos.string === userPos.string && correctPos.fret === userPos.fret
          )
        ).length;
        isCorrect = foundCount >= Math.ceil(currentQuestion.correctPositions.length / 2);
        break;
    }

    setFeedback(isCorrect ? 'correct' : 'incorrect');
    setShowAnswer(true);
    setHighlightedFretPositions(currentQuestion.correctPositions);

    // Update score
    const newTotal = currentScore.total + 1;
    const newCorrect = currentScore.correct + (isCorrect ? 1 : 0);
    const newStreak = isCorrect ? currentScore.streak + 1 : 0;
    console.log(`Quiz checkAnswer - Current: ${currentScore.total}, New Total: ${newTotal}, Correct: ${newCorrect}`);
    updateScore(newCorrect, newTotal, newStreak);
  };

  // Next question
  const nextQuestion = () => {
    // If there's a current question and the user has made attempts, check the answer first
    if (currentQuestion && userFretboardAnswers.length > 0 && !feedback) {
      checkAnswer();
      
      // Generate new question after score is updated to avoid race conditions
      setTimeout(() => {
        generateNewQuestion();
      }, 50);
    } else {
      generateNewQuestion();
    }
  };

  // Handle show answer button click
  const handleShowAnswer = () => {
    if (!currentQuestion) return;
    
    // Highlight all correct positions for this question
    setHighlightedFretPositions(currentQuestion.correctPositions);
    
    // For "find all notes" questions, also update the correct/incorrect arrays
    if (currentQuestion.questionType === 'findAllNotes') {
      setCorrectUserFretboardAnswers([]);
      setIncorrectUserFretboardAnswers([]);
    }
  };

  // Generate first question on mount
  useEffect(() => {
    generateNewQuestion();
  }, []);


  // Check for immediate feedback when user clicks a fret
  useEffect(() => {
    if (!currentQuestion) return;
    
    if (userFretboardAnswers.length === 0) {
      setImmediateFeedback(null);
      setLocalCorrectAnswers([]);
      setLocalIncorrectAnswers([]);
      // Only clear highlights if we're not in a "findAllNotes" question
      if (currentQuestion.questionType !== 'findAllNotes') {
        setHighlightedFretPositions([]);
      }
      return;
    }

    const lastAnswer = userFretboardAnswers[userFretboardAnswers.length - 1];
    const isCorrect = currentQuestion.correctPositions.some(correctPos =>
      correctPos.string === lastAnswer.string && correctPos.fret === lastAnswer.fret
    );

    setImmediateFeedback({ position: lastAnswer, isCorrect });
    
    // For single string questions, if answer is correct, don't auto-progress
    // User can manually click Next Question when ready

    // Always update local answers arrays for both question types
    const correctUserAnswers = userFretboardAnswers.filter(userPos =>
      currentQuestion.correctPositions.some(correctPos =>
        correctPos.string === userPos.string && correctPos.fret === userPos.fret
      )
    );
    const incorrectUserAnswers = userFretboardAnswers.filter(userPos =>
      !currentQuestion.correctPositions.some(correctPos =>
        correctPos.string === userPos.string && correctPos.fret === userPos.fret
      )
    );
    
    // Update local state for UI display
    setLocalCorrectAnswers(correctUserAnswers);
    setLocalIncorrectAnswers(incorrectUserAnswers);
    
    // For "findAllNotes" questions, also update parent state for fretboard rendering
    if (currentQuestion.questionType === 'findAllNotes') {
      setCorrectUserFretboardAnswers(correctUserAnswers);
      setIncorrectUserFretboardAnswers(incorrectUserAnswers);
      // Clear highlightedFretPositions since we're using correct/incorrect arrays now
      setHighlightedFretPositions([]);
    }
  }, [userFretboardAnswers, currentQuestion]);

  // Get question text
  const getQuestionText = (): string => {
    if (!currentQuestion) return '';

    switch (currentQuestion.questionType) {
      case 'findNoteOnString':
        return `Find ${currentQuestion.targetNote} on the ${currentQuestion.targetStringName} string`;
      case 'findAllNotes':
        return `Find all ${currentQuestion.targetNote} notes on the fretboard`;
      default:
        return '';
    }
  };

  // Get instructions text
  const getInstructionsText = (): string => {
    if (!currentQuestion) return '';

    switch (currentQuestion.questionType) {
      case 'findNoteOnString':
        return 'Click on the correct fret position';
      case 'findAllNotes':
        return 'Click on all positions where this note appears';
      default:
        return '';
    }
  };

  // Clear all user answers
  const handleClear = () => {
    setUserFretboardAnswers([]);
    setCorrectUserFretboardAnswers([]);
    setIncorrectUserFretboardAnswers([]);
    setHighlightedFretPositions([]);
    setLocalCorrectAnswers([]);
    setLocalIncorrectAnswers([]);
    setImmediateFeedback(null);
  };

  // Notify parent of quiz state changes (for mobile banner rendering)
  useEffect(() => {
    if (onQuizStateChange && currentQuestion) {
      onQuizStateChange({
        questionText: getQuestionText(),
        score: { correct: currentScore.correct, total: currentScore.total },
        feedback: immediateFeedback?.isCorrect === true ? 'correct' : immediateFeedback?.isCorrect === false ? 'incorrect' : null,
        hasAnswers: userFretboardAnswers.length > 0,
        onNext: nextQuestion,
        onShowAnswer: handleShowAnswer,
        onClear: handleClear
      });
    }
  }, [currentQuestion, currentScore, immediateFeedback, userFretboardAnswers.length, onQuizStateChange]);

  if (!currentQuestion) {
    return (
      <Card>
        <CardContent className="p-6 text-center">
          <p>Loading question...</p>
        </CardContent>
      </Card>
    );
  }

  // Mobile: Parent renders the banner, so we return null
  if (isMobile) {
    return null;
  }

  // Desktop: Show full Card-based UI
  return (
    <div className="space-y-4">
      {/* Question Display */}
      <Card className="p-4">
        <div className="text-center space-y-3">
          <div className="flex items-center justify-center gap-2">
            <Target className="h-5 w-5 text-blue-600" />
            <h3 className="text-lg font-semibold">{getQuestionText()}</h3>
          </div>
          <p className="text-sm text-muted-foreground">{getInstructionsText()}</p>

        </div>
      </Card>


      {/* User Progress - Single Answer Questions Only */}
      {currentQuestion?.questionType !== 'findAllNotes' && !showAnswer && (
        <Card className={`p-3 ${
          immediateFeedback 
            ? (immediateFeedback.isCorrect ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200')
            : 'bg-yellow-50 border-yellow-200'
        }`}>
          <div className="text-center space-y-2">
            {immediateFeedback ? (
              <div className="flex items-center justify-center gap-2">
                {immediateFeedback.isCorrect ? (
                  <CheckCircle className="h-5 w-5 text-green-600" />
                ) : (
                  <XCircle className="h-5 w-5 text-red-600" />
                )}
                <p className={`text-sm font-medium ${
                  immediateFeedback.isCorrect ? 'text-green-800' : 'text-red-800'
                }`}>
                  {immediateFeedback.isCorrect ? 'Correct!' : 'Try again!'}
                </p>
              </div>
            ) : (
              <p className="text-sm text-yellow-800">
                You've selected {userFretboardAnswers.length} position{userFretboardAnswers.length !== 1 ? 's' : ''}
              </p>
            )}
            
            <div className="flex flex-wrap justify-center gap-1 mt-2">
              {!immediateFeedback && userFretboardAnswers.length > 0 && (
                <Button size="sm" onClick={checkAnswer}>
                  Submit Answer
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={nextQuestion}>
                Next Question
              </Button>
              <Button size="sm" variant="outline" onClick={handleShowAnswer}>
                Show Answer
              </Button>
              {userFretboardAnswers.length > 0 && (
                <Button size="sm" variant="outline" onClick={() => {
                  setUserFretboardAnswers([]);
                  setCorrectUserFretboardAnswers([]);
                  setIncorrectUserFretboardAnswers([]);
                  setHighlightedFretPositions([]);
                  setLocalCorrectAnswers([]);
                  setLocalIncorrectAnswers([]);
                  setImmediateFeedback(null);
                }}>
                  Clear
                </Button>
              )}
            </div>
          </div>
        </Card>
      )}

      {/* User Progress for Find All Notes Questions */}
      {currentQuestion?.questionType === 'findAllNotes' && !showAnswer && (
        <Card className="p-3 bg-blue-50 border-blue-200">
          <div className="text-center space-y-2">
            <p className="text-sm text-blue-800 font-medium">
              Found {localCorrectAnswers.length} out of {currentQuestion.correctPositions.length} positions
            </p>
            
            {/* Display cumulative selections */}
            {userFretboardAnswers.length > 0 && (
              <div className="text-xs text-blue-700 space-y-1">
                <p className="font-medium">Your selections:</p>
                <div className="flex flex-wrap justify-center gap-1">
                  {userFretboardAnswers.map((answer, index) => {
                    const isCorrect = currentQuestion.correctPositions.some(correctPos =>
                      correctPos.string === answer.string && correctPos.fret === answer.fret
                    );
                    return (
                      <Badge 
                        key={index} 
                        variant={isCorrect ? "default" : "destructive"}
                        className={`text-xs ${
                          isCorrect 
                            ? 'bg-green-100 text-green-800 border-green-300' 
                            : 'bg-red-100 text-red-800 border-red-300'
                        }`}
                      >
                        S{answer.string}F{answer.fret}
                      </Badge>
                    );
                  })}
                </div>
                <p className="text-xs">
                  <span className="text-green-700">{localCorrectAnswers.length} correct</span>
                  {localIncorrectAnswers.length > 0 && (
                    <span>, <span className="text-red-700">{localIncorrectAnswers.length} incorrect</span></span>
                  )}
                </p>
              </div>
            )}
            
            <div className="flex flex-wrap justify-center gap-1 mt-2">
              {userFretboardAnswers.length > 0 && (
                <Button size="sm" onClick={checkAnswer}>
                  Submit Answer
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={nextQuestion}>
                Next Question
              </Button>
              <Button size="sm" variant="outline" onClick={handleShowAnswer}>
                Show Answer
              </Button>
              {userFretboardAnswers.length > 0 && (
                <Button size="sm" variant="outline" onClick={() => {
                  setUserFretboardAnswers([]);
                  setCorrectUserFretboardAnswers([]);
                  setIncorrectUserFretboardAnswers([]);
                  setHighlightedFretPositions([]);
                  setLocalCorrectAnswers([]);
                  setLocalIncorrectAnswers([]);
                }}>
                  Clear All
                </Button>
              )}
            </div>
            
            {userFretboardAnswers.length === 0 && (
              <p className="text-xs text-blue-600 mt-2">
                Click on the fretboard to find all {currentQuestion.targetNote} notes
              </p>
            )}
          </div>
        </Card>
      )}

      {/* Answer Feedback */}
      {showAnswer && (
        <Card className={`p-4 ${
          feedback === 'correct' 
            ? 'bg-green-50 border-green-200' 
            : 'bg-red-50 border-red-200'
        }`}>
          <div className="text-center space-y-3">
            <div className="flex items-center justify-center gap-2">
              {feedback === 'correct' ? (
                <CheckCircle className="h-6 w-6 text-green-600" />
              ) : (
                <XCircle className="h-6 w-6 text-red-600" />
              )}
              <span className={`font-semibold ${
                feedback === 'correct' ? 'text-green-800' : 'text-red-800'
              }`}>
                {feedback === 'correct' ? 'Correct!' : 'Not quite...'}
              </span>
            </div>
            
            <div className="space-y-2">
              <p className="text-sm">
                Found {currentQuestion.correctPositions.length} position{currentQuestion.correctPositions.length !== 1 ? 's' : ''} for {currentQuestion.targetNote}
              </p>
              <div className="text-xs text-muted-foreground">
                Correct positions are highlighted on the fretboard
              </div>
            </div>

            <Button onClick={nextQuestion} className="mt-4">
              <RefreshCw className="h-4 w-4 mr-2" />
              Next Question
            </Button>
          </div>
        </Card>
      )}

      {/* Quick Start (when no answer submitted yet) */}
      {userFretboardAnswers.length === 0 && !showAnswer && (
        <Card className="p-4 bg-blue-50 border-blue-200">
          <div className="text-center space-y-2">
            <p className="text-sm text-blue-800 font-medium">Click on the fretboard to start!</p>
            <p className="text-xs text-blue-600">
              {currentQuestion.questionType === 'findAllNotes' 
                ? 'You can click multiple positions for "find all" questions'
                : 'Click on any fret position to select it'
              }
            </p>
          </div>
        </Card>
      )}
    </div>
  );
};

export default NoteRecognitionQuiz;